/**
 * Design System Tokens - 競馬予測システム
 * プロフェッショナル基準のデザイントークン定義
 */

export const tokens = {
  // カラーパレット - アクセシビリティ準拠
  colors: {
    // プライマリカラー - 信頼性と専門性を表現
    primary: {
      50: '#e6f1ff',
      100: '#b3d4ff',
      200: '#80b7ff',
      300: '#4d9aff',
      400: '#1a7dff',
      500: '#0066e6', // Main - WCAG AA準拠
      600: '#0052b8',
      700: '#003d8a',
      800: '#00295c',
      900: '#00142e',
    },
    
    // セカンダリカラー - データ視覚化用
    secondary: {
      50: '#f0e6ff',
      100: '#d1b3ff',
      200: '#b380ff',
      300: '#944dff',
      400: '#751aff',
      500: '#5c00e6',
      600: '#4a00b8',
      700: '#37008a',
      800: '#25005c',
      900: '#12002e',
    },
    
    // 成功・警告・エラー - セマンティックカラー
    success: {
      50: '#e6f9f0',
      100: '#b3ecd4',
      200: '#80dfb8',
      300: '#4dd29c',
      400: '#1ac580',
      500: '#00b866', // WCAG AA準拠
      600: '#009352',
      700: '#006e3d',
      800: '#004a29',
      900: '#002514',
    },
    
    warning: {
      50: '#fff9e6',
      100: '#ffecb3',
      200: '#ffdf80',
      300: '#ffd24d',
      400: '#ffc51a',
      500: '#ffb800',
      600: '#cc9300',
      700: '#996e00',
      800: '#664a00',
      900: '#332500',
    },
    
    error: {
      50: '#ffebe6',
      100: '#ffc7b3',
      200: '#ffa380',
      300: '#ff7f4d',
      400: '#ff5b1a',
      500: '#e64500',
      600: '#b83700',
      700: '#8a2900',
      800: '#5c1b00',
      900: '#2e0d00',
    },
    
    // グレースケール - UI要素用
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    
    // 背景色
    background: {
      primary: '#ffffff',
      secondary: '#f9fafb',
      tertiary: '#f3f4f6',
      elevated: '#ffffff',
      overlay: 'rgba(0, 0, 0, 0.5)',
    },
    
    // テキストカラー - コントラスト比準拠
    text: {
      primary: '#111827', // コントラスト比 15.8:1
      secondary: '#4b5563', // コントラスト比 7.4:1
      tertiary: '#6b7280', // コントラスト比 4.5:1
      disabled: '#9ca3af',
      inverse: '#ffffff',
    },
  },
  
  // タイポグラフィ - 視覚階層の明確化
  typography: {
    fontFamily: {
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans JP", sans-serif',
      mono: 'ui-monospace, "SF Mono", Consolas, monospace',
    },
    
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem', // 48px
    },
    
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
    
    letterSpacing: {
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
    },
  },
  
  // スペーシング - 8pxグリッドシステム
  spacing: {
    0: '0',
    1: '0.25rem', // 4px
    2: '0.5rem', // 8px
    3: '0.75rem', // 12px
    4: '1rem', // 16px
    5: '1.25rem', // 20px
    6: '1.5rem', // 24px
    8: '2rem', // 32px
    10: '2.5rem', // 40px
    12: '3rem', // 48px
    16: '4rem', // 64px
    20: '5rem', // 80px
    24: '6rem', // 96px
  },
  
  // ボーダー
  borders: {
    radius: {
      none: '0',
      sm: '0.125rem', // 2px
      base: '0.25rem', // 4px
      md: '0.375rem', // 6px
      lg: '0.5rem', // 8px
      xl: '0.75rem', // 12px
      '2xl': '1rem', // 16px
      full: '9999px',
    },
    
    width: {
      0: '0',
      1: '1px',
      2: '2px',
      4: '4px',
    },
  },
  
  // シャドウ - エレベーション表現
  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    base: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    md: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    lg: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    none: 'none',
  },
  
  // アニメーション
  transitions: {
    duration: {
      fast: '150ms',
      base: '300ms',
      slow: '500ms',
    },
    
    timing: {
      ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
  
  // ブレイクポイント - モバイルファースト
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  // Zインデックス
  zIndex: {
    0: 0,
    10: 10,
    20: 20,
    30: 30,
    40: 40,
    50: 50,
    dropdown: 1000,
    sticky: 1020,
    modal: 1030,
    popover: 1040,
    tooltip: 1050,
  },
};

// セマンティックトークン - 用途別の定義
export const semanticTokens = {
  // 還元率表示用のカラースケール
  returnRate: {
    excellent: tokens.colors.success[500], // 95%以上
    good: tokens.colors.primary[500], // 85-95%
    average: tokens.colors.warning[500], // 75-85%
    poor: tokens.colors.error[500], // 75%未満
  },
  
  // インタラクティブ要素の状態
  interactive: {
    default: tokens.colors.primary[500],
    hover: tokens.colors.primary[600],
    active: tokens.colors.primary[700],
    disabled: tokens.colors.gray[300],
    focus: {
      ring: tokens.colors.primary[500],
      offset: '2px',
    },
  },
  
  // データ視覚化パレット
  dataViz: {
    primary: tokens.colors.primary[500],
    secondary: tokens.colors.secondary[500],
    tertiary: tokens.colors.success[500],
    quaternary: tokens.colors.warning[500],
    quinary: tokens.colors.error[500],
  },
};

// ユーティリティ関数
export const utils = {
  // レスポンシブ値を生成
  responsive: (values) => {
    const [sm, md, lg, xl] = values;
    return {
      base: sm,
      md: md || sm,
      lg: lg || md || sm,
      xl: xl || lg || md || sm,
    };
  },
  
  // カラーのアルファ値を調整
  alpha: (color, opacity) => {
    return color.replace(')', `, ${opacity})`).replace('rgb', 'rgba');
  },
  
  // アクセシブルなフォーカススタイル
  focusRing: () => ({
    outline: 'none',
    boxShadow: `0 0 0 2px ${tokens.colors.background.primary}, 0 0 0 4px ${semanticTokens.interactive.focus.ring}`,
  }),
  
  // スクリーンリーダー専用スタイル
  srOnly: () => ({
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: '0',
  }),
};

export default { tokens, semanticTokens, utils };